/**
 * Helperbird: Accessibility & Productivity App — dyslexia fonts, reader, OCR
 *
 * Declares one large web-accessible-resource entry matched to every http(s) origin with
 * `use_dynamic_url: false`, exposing dozens of named files — logos, fonts, cursor
 * images and page entry points such as `dictionary.html` and `settings.html`. All are
 * reachable from any http(s) page at a fixed URL.
 *
 * The vendor-named "happy" logo is the cleanest signal: it loads through `probeImage`
 * with no CORS grant, so it survives a restrictive page CSP.
 *
 * Manifest verified against version 2026.7.18.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'ahmapmilbkfamljbpgphfndeemhnajme';

export const helperbird: ExtensionDefinition = {
  id: ID,
  name: 'Helperbird: Accessibility & Productivity App',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 100_000,
  notes:
    'Dozens of named files exposed to every http(s) origin with use_dynamic_url:false. ' +
    'The vendor-named logo loads without a CORS grant, so detection is conclusive.',
  signals: [
    {
      id: 'war-helperbird-happy-logo',
      kind: 'web-accessible-resource',
      description:
        'Load assets/images/logo/transparent/fun/helperbird_happy.png via <img> — a ' +
        'vendor-named logo exposed to every http(s) origin; needs no CORS grant.',
      weight: 1,
      run: () =>
        probeImage(ID, 'assets/images/logo/transparent/fun/helperbird_happy.png'),
    },
    {
      id: 'war-logo-128',
      kind: 'web-accessible-resource',
      description:
        'Load assets/images/logo/transparent/128.png via <img> — the 128px logo, ' +
        'corroborating the identification.',
      weight: 0.5,
      run: () => probeImage(ID, 'assets/images/logo/transparent/128.png'),
    },
    {
      id: 'war-dictionary-html',
      kind: 'web-accessible-resource',
      description:
        'Fetch dictionary.html — a named feature page, independent of the image paths.',
      weight: 0.5,
      run: () => probeFetch(ID, 'dictionary.html'),
    },
  ],
};
